import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { GlassButton } from './ui/GlassButton';
import { GlassSelect } from './ui/GlassSelect';
import {
  LandingPage,
  Campaign,
  updateLandingPage,
  getLandingPageCampaigns,
  getLandingPageCampaignWorkflowCount,
} from '../services/api';

interface LandingPageCampaignModalProps {
  page: LandingPage;
  onClose: () => void;
  onSaved: (updated: LandingPage) => void;
}

/**
 * Link/unlink a landing page to a campaign. Linking a campaign is what makes
 * the landing page's native lead-capture widget (`submit_lead`) automatically
 * enroll new leads into any active NurtureWorkflow for that campaign with
 * trigger `form_submit` (campaign-sequence-enrollment feature).
 *
 * Visually mirrors the SEO settings modal in `LandingPagesList.tsx` — reuses
 * its exact class names (`.seo-modal-overlay`, `.seo-modal`, …) rather than
 * introducing parallel CSS.
 */
const LandingPageCampaignModal: React.FC<LandingPageCampaignModalProps> = ({
  page,
  onClose,
  onSaved,
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>(page.campaign_id || '');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [campaignsLoadFailed, setCampaignsLoadFailed] = useState(false);
  const [matchingWorkflowCount, setMatchingWorkflowCount] = useState<number | null>(null);
  const [workflowCheckFailed, setWorkflowCheckFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setCampaignsLoadFailed(false);
    getLandingPageCampaigns(page.project_id)
      .then((data) => {
        if (!cancelled) setCampaigns(data || []);
      })
      .catch(() => {
        if (!cancelled) {
          setCampaigns([]);
          setCampaignsLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [page.project_id]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedCampaignId) {
      setMatchingWorkflowCount(null);
      setWorkflowCheckFailed(false);
      return;
    }
    setWorkflowCheckFailed(false);
    getLandingPageCampaignWorkflowCount(selectedCampaignId, page.project_id)
      .then((count) => {
        if (!cancelled) setMatchingWorkflowCount(count);
      })
      .catch(() => {
        // code-review iteration 1 IMPR-6 fix: distinguish "still loading" from
        // "the check failed" — previously both set matchingWorkflowCount back
        // to null, so a 403/network error rendered "Checking nurture
        // workflows…" forever instead of a visible error.
        if (!cancelled) {
          setMatchingWorkflowCount(null);
          setWorkflowCheckFailed(true);
        }
      });
    return () => { cancelled = true; };
  }, [selectedCampaignId, page.project_id]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Send an explicit `null` to clear the link — an omitted/undefined key
      // is dropped by exclude_unset on the backend and would leave the old
      // campaign_id in place instead of unlinking it.
      const updated = await updateLandingPage(page.landing_page_id, {
        campaign_id: (selectedCampaignId || null) as any,
      });
      onSaved(updated);
      onClose();
    } catch (err) {
      alert('Failed to save campaign link');
    } finally {
      setIsSaving(false);
    }
  };

  const options = [
    { value: '', label: '— No campaign —' },
    ...campaigns.map((c) => ({ value: c.campaign_id, label: c.name })),
  ];

  return (
    <div className="seo-modal-overlay" onClick={onClose}>
      <div className="seo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="seo-modal-header">
          <h2>Campaign — {page.title}</h2>
          <button className="seo-modal-close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
        <div className="seo-modal-body">
          <div className="seo-field-group">
            <label>Campaign</label>
            <GlassSelect
              options={options}
              value={selectedCampaignId}
              onChange={setSelectedCampaignId}
              placeholder={isLoading ? 'Loading campaigns…' : '— No campaign —'}
              disabled={isLoading}
            />
          </div>
          {campaignsLoadFailed && (
            <p style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>
              Couldn't load campaigns for this project.
            </p>
          )}
          {selectedCampaignId && (
            <p style={{ fontSize: '0.8rem', color: workflowCheckFailed ? 'var(--danger)' : 'var(--text-secondary)' }}>
              {workflowCheckFailed
                ? "Couldn't check nurture workflows for this campaign."
                : matchingWorkflowCount === null
                ? 'Checking nurture workflows…'
                : matchingWorkflowCount === 0
                ? 'No active workflows will fire on form submit for this campaign yet.'
                : `${matchingWorkflowCount} active workflow${matchingWorkflowCount === 1 ? '' : 's'} will fire on form submit.`}
            </p>
          )}
        </div>
        <div className="seo-modal-footer">
          <GlassButton variant="primary" onClick={handleSave} disabled={isSaving} loading={isSaving}>
            Save
          </GlassButton>
          <GlassButton variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default LandingPageCampaignModal;
